// services/TrailDiscoveryService.ts
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  addDoc,
  updateDoc,
  deleteDoc,
  setDoc,
  serverTimestamp,
  Timestamp,
  writeBatch,
} from 'firebase/firestore';
import { db } from '@/firebase/clientApp';
import {
  EnhancedTrailData,
  PointOfInterest,
  Season,
  WeatherCondition,
  POITrailConnection,
  WeatherActivity,
  SeasonalActivity,
} from '@/types/enhancedTrail';
import { TrailWaypoint } from '@/types/hikingTrail';

/**
 * Service for managing enhanced trail data, POIs, weather activities, and seasonal highlights
 * for the trail discovery feature
 */
export class TrailDiscoveryService {
  private readonly trailsCollection = 'trails';
  private readonly waypointsCollection = 'waypoints';
  private readonly enhancedTrailsCollection = 'enhancedTrails';
  private readonly poisCollection = 'pois';
  private readonly weatherActivitiesCollection = 'weatherActivities';
  private readonly seasonalActivitiesCollection = 'seasonalActivities';

  /**
   * Convert Firestore timestamp to milliseconds
   */
  private convertTimestamp(timestamp: Timestamp | null | undefined): number {
    if (!timestamp) return Date.now();
    return timestamp.toMillis();
  }

  /**
   * Fallback method to get waypoints without requiring the composite index
   * This is used when the index is still building
   */
  private async getWaypointsWithoutIndex(
    trailId: string
  ): Promise<TrailWaypoint[]> {
    try {
      // Query waypoints only with the trailId filter (no ordering)
      const waypointsQuery = query(
        collection(db, this.waypointsCollection),
        where('trailId', '==', trailId)
      );

      const waypointsSnapshot = await getDocs(waypointsQuery);
      const waypoints: TrailWaypoint[] = [];

      waypointsSnapshot.forEach((waypointDoc) => {
        const data = waypointDoc.data();
        waypoints.push({
          id: waypointDoc.id,
          trailId: data.trailId,
          name: data.name,
          description: data.description,
          coordinates: data.coordinates,
          order: data.order,
          timestamp: this.convertTimestamp(data.timestamp),
          photoUrl: data.photoUrl,
        });
      });

      // Sort the waypoints by order in memory
      return waypoints.sort((a, b) => {
        const orderA = a.order || 0;
        const orderB = b.order || 0;
        return orderA - orderB;
      });
    } catch (error) {
      console.error(
        `Error fetching waypoints without index for trail ${trailId}:`,
        error
      );
      return [];
    }
  }

  /***********************************************
   * CRUD Operations for Enhanced Trails
   ***********************************************/

  /**
   * Create a new enhanced trail or add enhanced data to an existing trail
   */
  async createEnhancedTrail(
    enhancedData: Partial<EnhancedTrailData>,
    trailId: string
  ): Promise<string> {
    try {
      // Verify that the trail exists first
      const trailDoc = await getDoc(doc(db, this.trailsCollection, trailId));

      if (!trailDoc.exists()) {
        throw new Error(`Trail with ID ${trailId} does not exist`);
      }

      const enhancedRef = doc(db, this.enhancedTrailsCollection, trailId);

      // Prepare enhanced data for storage
      const {
        id,
        name,
        description,
        createdAt,
        updatedAt,
        totalDistance,
        estimatedDuration,
        difficulty,
        tags,
        region,
        waypoints,
        ...enhancedFields
      } = enhancedData as any;

      // Convert any Date objects to Firestore timestamps and ensure all references are valid
      const sanitizedData = JSON.parse(
        JSON.stringify({
          ...enhancedFields,
          trailId,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        })
      );

      // Add the enhanced trail document
      await updateDoc(enhancedRef, sanitizedData).catch(() => {
        // If document doesn't exist, create it
        return setDoc(enhancedRef, sanitizedData);
      });

      return trailId;
    } catch (error) {
      console.error('Error creating enhanced trail:', error);
      throw error;
    }
  }

  /**
   * Update an enhanced trail
   */
  async updateEnhancedTrail(
    trailId: string,
    updates: Partial<EnhancedTrailData>
  ): Promise<void> {
    try {
      const enhancedRef = doc(db, this.enhancedTrailsCollection, trailId);

      // Remove properties that are part of the base trail
      const {
        id,
        name,
        description,
        createdAt,
        updatedAt,
        totalDistance,
        estimatedDuration,
        difficulty,
        tags,
        region,
        waypoints,
        ...enhancedFields
      } = updates as any;

      // Prepare update data
      const updateData = {
        ...enhancedFields,
        updatedAt: serverTimestamp(),
      };

      // Update enhanced trail document
      await updateDoc(enhancedRef, updateData);
    } catch (error) {
      console.error(`Error updating enhanced trail ${trailId}:`, error);
      throw error;
    }
  }

  /**
   * Delete a trail completely - removes base trail, enhanced data, and waypoints
   */
  async deleteEnhancedTrail(trailId: string): Promise<void> {
    try {
      // First, delete all waypoints for this trail
      const waypointsQuery = query(
        collection(db, this.waypointsCollection),
        where('trailId', '==', trailId)
      );
      const waypointsSnapshot = await getDocs(waypointsQuery);

      // Use a batch to delete all waypoints
      if (!waypointsSnapshot.empty) {
        const batch = writeBatch(db);
        waypointsSnapshot.docs.forEach((doc) => {
          batch.delete(doc.ref);
        });
        await batch.commit();
        console.log(
          `Deleted ${waypointsSnapshot.size} waypoints for trail ${trailId}`
        );
      }

      // Delete the enhanced trail data
      await deleteDoc(doc(db, this.enhancedTrailsCollection, trailId));

      // Delete the base trail data
      await deleteDoc(doc(db, this.trailsCollection, trailId));

      console.log(`Trail ${trailId} has been completely deleted`);
    } catch (error) {
      console.error(`Error deleting trail ${trailId}:`, error);
      throw error;
    }
  }

  /**
   * Get all enhanced trails
   */
  async getEnhancedTrails(): Promise<EnhancedTrailData[]> {
    try {
      // Get base trails first
      const trailsQuery = query(
        collection(db, this.trailsCollection),
        orderBy('createdAt', 'desc')
      );

      const trailsSnapshot = await getDocs(trailsQuery);
      const trails: EnhancedTrailData[] = [];

      // Process each trail
      for (const trailDoc of trailsSnapshot.docs) {
        const trailId = trailDoc.id;
        const trailData = trailDoc.data();

        // Get the corresponding enhanced data
        const enhancedDoc = await getDoc(
          doc(db, this.enhancedTrailsCollection, trailId)
        );
        const enhancedData = enhancedDoc.exists() ? enhancedDoc.data() : {};

        // Try to get waypoints using the index-required query
        let waypoints: TrailWaypoint[] = [];
        try {
          // This query requires the composite index that may still be building
          const waypointsQuery = query(
            collection(db, this.waypointsCollection),
            where('trailId', '==', trailId),
            orderBy('order')
          );

          const waypointsSnapshot = await getDocs(waypointsQuery);

          waypointsSnapshot.forEach((waypointDoc) => {
            const data = waypointDoc.data();
            waypoints.push({
              id: waypointDoc.id,
              trailId: data.trailId,
              name: data.name,
              description: data.description,
              coordinates: data.coordinates,
              order: data.order,
              timestamp: this.convertTimestamp(data.timestamp),
              photoUrl: data.photoUrl,
            });
          });
        } catch (error) {
          console.log(
            'Index error, using fallback method to get waypoints:',
            error
          );
          // Fallback: get all waypoints for this trail without ordering (doesn't require index)
          waypoints = await this.getWaypointsWithoutIndex(trailId);
        }

        // Create the enhanced trail object by combining base trail data and enhanced data
        const enhancedTrail: EnhancedTrailData = {
          id: trailDoc.id,
          name: trailData.name,
          description: trailData.description,
          difficulty: trailData.difficulty,
          region: trailData.region,
          createdAt: this.convertTimestamp(trailData.createdAt),
          updatedAt: this.convertTimestamp(trailData.updatedAt),
          totalDistance: trailData.totalDistance,
          estimatedDuration: trailData.estimatedDuration,
          tags: trailData.tags || [],
          waypoints,

          // Add enhanced properties
          weatherConsiderations: enhancedData.weatherConsiderations,
          bestSeasons: enhancedData.bestSeasons,
          seasonalNotes: enhancedData.seasonalNotes,
          elevationGain: enhancedData.elevationGain,
          elevationLoss: enhancedData.elevationLoss,
          highestPoint: enhancedData.highestPoint,
          lowestPoint: enhancedData.lowestPoint,
          pointsOfInterest: enhancedData.pointsOfInterest,
          popularity: enhancedData.popularity,
          scenicRating: enhancedData.scenicRating,
          familySuitability: enhancedData.familySuitability,
          technicalDifficulty: enhancedData.technicalDifficulty,
          shadeCoverage: enhancedData.shadeCoverage,
          surfaceType: enhancedData.surfaceType,
          requiredFitness: enhancedData.requiredFitness,
        };

        trails.push(enhancedTrail);
      }

      return trails;
    } catch (error) {
      console.error('Error fetching enhanced trails:', error);
      throw error;
    }
  }

  /**
   * Get a specific enhanced trail by ID
   */
  async getEnhancedTrail(trailId: string): Promise<EnhancedTrailData | null> {
    try {
      // Get base trail data
      const trailDoc = await getDoc(doc(db, this.trailsCollection, trailId));

      if (!trailDoc.exists()) {
        return null;
      }

      const trailData = trailDoc.data();

      // Get enhanced data
      const enhancedDoc = await getDoc(
        doc(db, this.enhancedTrailsCollection, trailId)
      );
      const enhancedData = enhancedDoc.exists() ? enhancedDoc.data() : {};

      // Get waypoints
      const waypointsQuery = query(
        collection(db, this.waypointsCollection),
        where('trailId', '==', trailId),
        orderBy('order')
      );

      const waypointsSnapshot = await getDocs(waypointsQuery);
      const waypoints: TrailWaypoint[] = [];

      waypointsSnapshot.forEach((waypointDoc) => {
        const data = waypointDoc.data();
        waypoints.push({
          id: waypointDoc.id,
          trailId: data.trailId,
          name: data.name,
          description: data.description,
          coordinates: data.coordinates,
          order: data.order,
          timestamp: this.convertTimestamp(data.timestamp),
          photoUrl: data.photoUrl,
        });
      });

      // Create the enhanced trail object
      return {
        id: trailDoc.id,
        name: trailData.name,
        description: trailData.description,
        difficulty: trailData.difficulty,
        region: trailData.region,
        createdAt: this.convertTimestamp(trailData.createdAt),
        updatedAt: this.convertTimestamp(trailData.updatedAt),
        totalDistance: trailData.totalDistance,
        estimatedDuration: trailData.estimatedDuration,
        tags: trailData.tags || [],
        waypoints,

        // Add enhanced properties
        weatherConsiderations: enhancedData.weatherConsiderations,
        bestSeasons: enhancedData.bestSeasons,
        seasonalNotes: enhancedData.seasonalNotes,
        elevationGain: enhancedData.elevationGain,
        elevationLoss: enhancedData.elevationLoss,
        highestPoint: enhancedData.highestPoint,
        lowestPoint: enhancedData.lowestPoint,
        pointsOfInterest: enhancedData.pointsOfInterest,
        popularity: enhancedData.popularity,
        scenicRating: enhancedData.scenicRating,
        familySuitability: enhancedData.familySuitability,
        technicalDifficulty: enhancedData.technicalDifficulty,
        shadeCoverage: enhancedData.shadeCoverage,
        surfaceType: enhancedData.surfaceType,
        requiredFitness: enhancedData.requiredFitness,
      };
    } catch (error) {
      console.error(`Error fetching enhanced trail ${trailId}:`, error);
      throw error;
    }
  }

  /***********************************************
   * CRUD Operations for Points of Interest
   ***********************************************/

  /**
   * Create a new point of interest
   */
  async createPOI(poiData: Partial<PointOfInterest>): Promise<string> {
    try {
      // Generate a unique ID for the POI if not provided
      const poiId =
        poiData.id ||
        `poi-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

      // Create POI document with the generated or provided ID
      const poiRef = doc(db, this.poisCollection, poiId);

      // Prepare POI data
      const sanitizedData = {
        ...poiData,
        id: poiId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      // Add the POI document
      await setDoc(poiRef, sanitizedData);

      return poiId;
    } catch (error) {
      console.error('Error creating POI:', error);
      throw error;
    }
  }

  /**
   * Update a point of interest
   */
  async updatePOI(
    poiId: string,
    updates: Partial<PointOfInterest>
  ): Promise<void> {
    try {
      const poiRef = doc(db, this.poisCollection, poiId);

      // Prepare update data
      const updateData = {
        ...updates,
        updatedAt: serverTimestamp(),
      };

      // Remove ID from updates if present
      if (updateData.id) {
        delete updateData.id;
      }

      // Update POI document
      await updateDoc(poiRef, updateData);
    } catch (error) {
      console.error(`Error updating POI ${poiId}:`, error);
      throw error;
    }
  }

  /**
   * Delete a point of interest
   */
  async deletePOI(poiId: string): Promise<void> {
    try {
      await deleteDoc(doc(db, this.poisCollection, poiId));

      // Also need to update any trails that reference this POI
      const trails = await this.getEnhancedTrails();

      for (const trail of trails) {
        if (trail.pointsOfInterest?.some((p) => p.poiId === poiId)) {
          // Filter out the deleted POI
          const updatedPOIs = trail.pointsOfInterest.filter(
            (p) => p.poiId !== poiId
          );

          // Update the trail
          await this.updateEnhancedTrail(trail.id, {
            pointsOfInterest: updatedPOIs,
          });
        }
      }
    } catch (error) {
      console.error(`Error deleting POI ${poiId}:`, error);
      throw error;
    }
  }

  /**
   * Get all points of interest
   */
  async getPointsOfInterest(): Promise<PointOfInterest[]> {
    try {
      const poisQuery = query(collection(db, this.poisCollection));
      const poisSnapshot = await getDocs(poisQuery);
      const pois: PointOfInterest[] = [];

      poisSnapshot.forEach((poiDoc) => {
        const data = poiDoc.data();
        pois.push({
          id: poiDoc.id,
          name: data.name,
          description: data.description,
          type: data.type,
          coordinates: data.coordinates,
          imageUrl: data.imageUrl,
          openingHours: data.openingHours,
          contactInfo: data.contactInfo,
          website: data.website,
        });
      });

      return pois;
    } catch (error) {
      console.error('Error fetching points of interest:', error);
      throw error;
    }
  }

  /**
   * Get a specific point of interest by ID
   */
  async getPOI(poiId: string): Promise<PointOfInterest | null> {
    try {
      const poiDoc = await getDoc(doc(db, this.poisCollection, poiId));

      if (!poiDoc.exists()) {
        return null;
      }

      return poiDoc.data() as PointOfInterest;
    } catch (error) {
      console.error(`Error fetching POI ${poiId}:`, error);
      throw error;
    }
  }

  /**
   * Get POIs by type
   */
  async getPOIsByType(poiType: string): Promise<PointOfInterest[]> {
    try {
      const poisQuery = query(
        collection(db, this.poisCollection),
        where('type', '==', poiType)
      );

      const poisSnapshot = await getDocs(poisQuery);
      const pois: PointOfInterest[] = [];

      poisSnapshot.forEach((poiDoc) => {
        pois.push(poiDoc.data() as PointOfInterest);
      });

      return pois;
    } catch (error) {
      console.error(`Error fetching POIs of type ${poiType}:`, error);
      throw error;
    }
  }

  /**
   * Connect a POI to a trail
   */
  async connectPOIToTrail(
    trailId: string,
    poiId: string,
    connectionData: Omit<POITrailConnection, 'poiId'>
  ): Promise<void> {
    try {
      // Get the current trail data
      const trail = await this.getEnhancedTrail(trailId);

      if (!trail) {
        throw new Error(`Trail with ID ${trailId} not found`);
      }

      // Check if the POI exists
      const poi = await this.getPOI(poiId);

      if (!poi) {
        throw new Error(`POI with ID ${poiId} not found`);
      }

      // Create the connection object
      const connection: POITrailConnection = {
        poiId,
        distanceFromStart: connectionData.distanceFromStart,
        detourDistance: connectionData.detourDistance,
        detourDuration: connectionData.detourDuration,
      };

      // Update the trail's pointsOfInterest array
      const pointsOfInterest = trail.pointsOfInterest || [];

      // Check if the connection already exists
      const existingIndex = pointsOfInterest.findIndex(
        (p) => p.poiId === poiId
      );

      if (existingIndex >= 0) {
        // Update existing connection
        pointsOfInterest[existingIndex] = connection;
      } else {
        // Add new connection
        pointsOfInterest.push(connection);
      }

      // Update the trail
      await this.updateEnhancedTrail(trailId, { pointsOfInterest });
    } catch (error) {
      console.error(
        `Error connecting POI ${poiId} to trail ${trailId}:`,
        error
      );
      throw error;
    }
  }

  /**
   * Remove a POI from a trail
   */
  async removePOIFromTrail(trailId: string, poiId: string): Promise<void> {
    try {
      // Get the current trail data
      const trail = await this.getEnhancedTrail(trailId);

      if (!trail || !trail.pointsOfInterest) {
        return; // Nothing to remove
      }

      // Filter out the POI
      const updatedPOIs = trail.pointsOfInterest.filter(
        (p) => p.poiId !== poiId
      );

      // Update the trail
      await this.updateEnhancedTrail(trailId, {
        pointsOfInterest: updatedPOIs,
      });
    } catch (error) {
      console.error(
        `Error removing POI ${poiId} from trail ${trailId}:`,
        error
      );
      throw error;
    }
  }

  /***********************************************
   * CRUD Operations for Weather Activities
   ***********************************************/

  /**
   * Create a new weather activity
   */
  async createWeatherActivity(
    weatherCondition: WeatherCondition,
    activityData: Partial<WeatherActivity>
  ): Promise<string> {
    try {
      // Generate an ID if not provided
      const activityId =
        activityData.id ||
        `weather-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

      // Create the full activity object
      const activity: WeatherActivity = {
        id: activityId,
        name: activityData.name || 'New Activity',
        description: activityData.description || '',
        location: activityData.location || '',
        indoorActivity: activityData.indoorActivity || false,
        duration: activityData.duration,
        familyFriendly: activityData.familyFriendly,
        website: activityData.website,
        openingHours: activityData.openingHours,
        imageUrl: activityData.imageUrl,
        distance: activityData.distance,
        difficulty: activityData.difficulty,
        trailReference: activityData.trailReference,
        contactInfo: activityData.contactInfo,
      };

      // Add to Firestore under the weatherActivities collection
      const docRef = doc(
        db,
        this.weatherActivitiesCollection,
        weatherCondition
      );

      // Get current activities for this weather condition
      const docSnap = await getDoc(docRef);
      let activities: WeatherActivity[] = [];

      if (docSnap.exists()) {
        activities = docSnap.data().activities || [];
      }

      // Add the new activity
      activities.push(activity);

      // Update or create the document
      if (docSnap.exists()) {
        await updateDoc(docRef, { activities });
      } else {
        await setDoc(docRef, {
          condition: weatherCondition,
          activities,
          updatedAt: serverTimestamp(),
        });
      }

      return activityId;
    } catch (error) {
      console.error(
        `Error creating weather activity for ${weatherCondition}:`,
        error
      );
      throw error;
    }
  }

  /**
   * Get all weather activities for a specific condition
   */
  async getWeatherActivitiesForCondition(
    weatherCondition: WeatherCondition
  ): Promise<WeatherActivity[]> {
    try {
      const docRef = doc(
        db,
        this.weatherActivitiesCollection,
        weatherCondition
      );
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return [];
      }

      return docSnap.data().activities || [];
    } catch (error) {
      console.error(
        `Error fetching weather activities for ${weatherCondition}:`,
        error
      );
      throw error;
    }
  }

  /**
   * Get all weather activities
   */
  async getAllWeatherActivities(): Promise<
    Record<WeatherCondition, WeatherActivity[]>
  > {
    try {
      const result: Record<WeatherCondition, WeatherActivity[]> = {} as any;
      const conditions: WeatherCondition[] = [
        'sunny',
        'cloudy',
        'rainy',
        'snowy',
        'foggy',
        'windy',
        'light_rain',
        'clear',
      ];

      for (const condition of conditions) {
        result[condition] =
          await this.getWeatherActivitiesForCondition(condition);
      }

      return result;
    } catch (error) {
      console.error('Error fetching all weather activities:', error);
      throw error;
    }
  }

  /**
   * Update a weather activity
   */
  async updateWeatherActivity(
    weatherCondition: WeatherCondition,
    activityId: string,
    updates: Partial<WeatherActivity>
  ): Promise<void> {
    try {
      const docRef = doc(
        db,
        this.weatherActivitiesCollection,
        weatherCondition
      );
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error(
          `No activities found for weather condition: ${weatherCondition}`
        );
      }

      let activities = docSnap.data().activities || [];
      const activityIndex = activities.findIndex(
        (a: WeatherActivity) => a.id === activityId
      );

      if (activityIndex === -1) {
        throw new Error(`Activity with ID ${activityId} not found`);
      }

      // Update the activity
      activities[activityIndex] = {
        ...activities[activityIndex],
        ...updates,
      };

      // Update the document
      await updateDoc(docRef, {
        activities,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error(`Error updating weather activity ${activityId}:`, error);
      throw error;
    }
  }

  /**
   * Delete a weather activity
   */
  async deleteWeatherActivity(
    weatherCondition: WeatherCondition,
    activityId: string
  ): Promise<void> {
    try {
      const docRef = doc(
        db,
        this.weatherActivitiesCollection,
        weatherCondition
      );
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return; // Nothing to delete
      }

      let activities = docSnap.data().activities || [];
      activities = activities.filter(
        (a: WeatherActivity) => a.id !== activityId
      );

      // Update the document
      await updateDoc(docRef, {
        activities,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error(`Error deleting weather activity ${activityId}:`, error);
      throw error;
    }
  }

  /***********************************************
   * CRUD Operations for Seasonal Activities
   ***********************************************/

  /**
   * Create a new seasonal activity
   */
  async createSeasonalActivity(
    season: Season,
    activityData: Partial<SeasonalActivity>
  ): Promise<string> {
    try {
      // Generate an ID if not provided
      const activityId =
        activityData.id ||
        `season-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

      // Create the full activity object
      const activity: SeasonalActivity = {
        id: activityId,
        name: activityData.name || 'New Seasonal Activity',
        description: activityData.description || '',
        bestTime: activityData.bestTime || '',
        location: activityData.location || '',
        distance: activityData.distance,
        duration: activityData.duration,
        weatherDependency: activityData.weatherDependency,
        familyFriendly: activityData.familyFriendly,
        trailReference: activityData.trailReference,
        relatedTrail: activityData.relatedTrail,
      };

      // Add to Firestore under the seasonalActivities collection
      const docRef = doc(db, this.seasonalActivitiesCollection, season);

      // Get current activities for this season
      const docSnap = await getDoc(docRef);
      let activities: SeasonalActivity[] = [];

      if (docSnap.exists()) {
        activities = docSnap.data().activities || [];
      }

      // Add the new activity
      activities.push(activity);

      // Update or create the document
      if (docSnap.exists()) {
        await updateDoc(docRef, { activities });
      } else {
        await setDoc(docRef, {
          season,
          activities,
          updatedAt: serverTimestamp(),
        });
      }

      return activityId;
    } catch (error) {
      console.error(`Error creating seasonal activity for ${season}:`, error);
      throw error;
    }
  }

  /**
   * Get all seasonal activities for a specific season
   */
  async getSeasonalActivitiesForSeason(
    season: Season
  ): Promise<SeasonalActivity[]> {
    try {
      const docRef = doc(db, this.seasonalActivitiesCollection, season);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return [];
      }

      return docSnap.data().activities || [];
    } catch (error) {
      console.error(`Error fetching seasonal activities for ${season}:`, error);
      throw error;
    }
  }

  /**
   * Get all seasonal activities
   */
  async getAllSeasonalActivities(): Promise<
    Record<Season, SeasonalActivity[]>
  > {
    try {
      const result: Record<Season, SeasonalActivity[]> = {} as any;
      const seasons: Season[] = ['spring', 'summer', 'autumn', 'winter'];

      for (const season of seasons) {
        result[season] = await this.getSeasonalActivitiesForSeason(season);
      }

      return result;
    } catch (error) {
      console.error('Error fetching all seasonal activities:', error);
      throw error;
    }
  }

  /**
   * Update a seasonal activity
   */
  async updateSeasonalActivity(
    season: Season,
    activityId: string,
    updates: Partial<SeasonalActivity>
  ): Promise<void> {
    try {
      const docRef = doc(db, this.seasonalActivitiesCollection, season);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error(`No activities found for season: ${season}`);
      }

      let activities = docSnap.data().activities || [];
      const activityIndex = activities.findIndex(
        (a: SeasonalActivity) => a.id === activityId
      );

      if (activityIndex === -1) {
        throw new Error(`Activity with ID ${activityId} not found`);
      }

      // Update the activity
      activities[activityIndex] = {
        ...activities[activityIndex],
        ...updates,
      };

      // Update the document
      await updateDoc(docRef, {
        activities,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error(`Error updating seasonal activity ${activityId}:`, error);
      throw error;
    }
  }

  /**
   * Delete a seasonal activity
   */
  async deleteSeasonalActivity(
    season: Season,
    activityId: string
  ): Promise<void> {
    try {
      const docRef = doc(db, this.seasonalActivitiesCollection, season);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return; // Nothing to delete
      }

      let activities = docSnap.data().activities || [];
      activities = activities.filter(
        (a: SeasonalActivity) => a.id !== activityId
      );

      // Update the document
      await updateDoc(docRef, {
        activities,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error(`Error deleting seasonal activity ${activityId}:`, error);
      throw error;
    }
  }

  /**
   * Get filtered trails based on criteria
   */
  async getFilteredTrails(criteria: {
    difficulty?: string;
    minDistance?: number;
    maxDistance?: number;
    season?: Season;
    surfaceType?: string;
    familySuitability?: boolean;
    limit?: number;
  }): Promise<EnhancedTrailData[]> {
    try {
      // First get all trails - we'll filter in memory since Firestore doesn't support all our filter criteria
      const allTrails = await this.getEnhancedTrails();

      // Apply filters
      let filteredTrails = allTrails;

      if (criteria.difficulty) {
        filteredTrails = filteredTrails.filter(
          (trail) => trail.difficulty === criteria.difficulty
        );
      }

      if (criteria.minDistance !== undefined) {
        filteredTrails = filteredTrails.filter(
          (trail) => (trail.totalDistance ?? 0) >= criteria.minDistance!
        );
      }

      if (criteria.maxDistance !== undefined) {
        filteredTrails = filteredTrails.filter(
          (trail) => (trail.totalDistance ?? 0) <= criteria.maxDistance!
        );
      }

      if (criteria.season) {
        filteredTrails = filteredTrails.filter(
          (trail) =>
            trail.bestSeasons && trail.bestSeasons.includes(criteria.season!)
        );
      }

      if (criteria.surfaceType) {
        filteredTrails = filteredTrails.filter(
          (trail) => trail.surfaceType === criteria.surfaceType
        );
      }

      if (criteria.familySuitability) {
        filteredTrails = filteredTrails.filter(
          (trail) =>
            trail.familySuitability !== undefined &&
            trail.familySuitability >= 7
        );
      }

      // Apply limit if provided
      if (criteria.limit && filteredTrails.length > criteria.limit) {
        filteredTrails = filteredTrails.slice(0, criteria.limit);
      }

      return filteredTrails;
    } catch (error) {
      console.error('Error fetching filtered trails:', error);
      throw error;
    }
  }

  /**
   * Get trails appropriate for specific weather conditions
   */
  async getWeatherAppropriateTrails(weatherConditions: {
    condition: WeatherCondition;
    temperature: number;
    isRaining: boolean;
    isSnowing: boolean;
    windSpeed: number;
  }): Promise<EnhancedTrailData[]> {
    try {
      const allTrails = await this.getEnhancedTrails();

      return allTrails.filter((trail) => {
        if (!trail.weatherConsiderations) return true;

        // Check temperature range
        if (
          trail.weatherConsiderations.minTemperature !== undefined &&
          weatherConditions.temperature <
            trail.weatherConsiderations.minTemperature
        ) {
          return false;
        }

        if (
          trail.weatherConsiderations.maxTemperature !== undefined &&
          weatherConditions.temperature >
            trail.weatherConsiderations.maxTemperature
        ) {
          return false;
        }

        // Check rain conditions
        if (
          trail.weatherConsiderations.avoidInRain &&
          weatherConditions.isRaining
        ) {
          return false;
        }

        // Check snow conditions
        if (
          trail.weatherConsiderations.avoidInSnow &&
          weatherConditions.isSnowing
        ) {
          return false;
        }

        // Check wind sensitivity
        if (
          trail.weatherConsiderations.windSensitive &&
          weatherConditions.windSpeed > 20
        ) {
          return false;
        }

        // Check suitable weather conditions
        if (
          trail.weatherConsiderations.suitableWeather &&
          !trail.weatherConsiderations.suitableWeather.includes(
            weatherConditions.condition
          )
        ) {
          return false;
        }

        return true;
      });
    } catch (error) {
      console.error('Error fetching weather-appropriate trails:', error);
      throw error;
    }
  }

  /**
   * Get trails for a specific season
   */
  async getSeasonalTrails(
    season: Season,
    limit?: number
  ): Promise<EnhancedTrailData[]> {
    try {
      const allTrails = await this.getEnhancedTrails();

      const seasonalTrails = allTrails.filter(
        (trail) => trail.bestSeasons && trail.bestSeasons.includes(season)
      );

      if (limit && seasonalTrails.length > limit) {
        return seasonalTrails.slice(0, limit);
      }

      return seasonalTrails;
    } catch (error) {
      console.error(`Error fetching seasonal trails for ${season}:`, error);
      throw error;
    }
  }
}

// Export a singleton instance
export const trailDiscoveryService = new TrailDiscoveryService();
